import { useState } from 'react';
import type { Category } from '../../../types';

interface CategoryPickerProps {
  categories: Category[];
  onComplete: (selectedIds: string[]) => Promise<void>;
}

export default function CategoryPicker({
  categories,
  onComplete,
}: CategoryPickerProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleContinue = async () => {
    setSaving(true);
    try {
      await onComplete(Array.from(selected));
    } catch (err) {
      console.error('Failed to save categories:', err);
    } finally {
      setSaving(false);
    }
  };

  // Filter out "Article of the Day" - it's always shown as hero
  const pickableCategories = categories.filter(
    (c) => c.name.toLowerCase() !== 'article of the day'
  );

  return (
    <div style={{ padding: '20px 10px' }}>
      <h2
        style={{
          margin: '0 0 8px 0',
          fontSize: '1.5rem',
          fontFamily: 'var(--font-display)',
          fontWeight: 400,
          color: 'var(--primary)',
          textAlign: 'center',
        }}
      >
        What interests you?
      </h2>
      <p
        style={{
          margin: '0 0 20px 0',
          fontSize: '0.9rem',
          color: 'var(--primary-soft)',
          textAlign: 'center',
        }}
      >
        Pick a few topics to personalize your feed
      </p>

      {/* Category grid */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          justifyContent: 'center',
          marginBottom: '24px',
        }}
      >
        {pickableCategories.map((category) => {
          const isSelected = selected.has(category.id);
          return (
            <button
              key={category.id}
              onClick={() => toggle(category.id)}
              style={{
                padding: '10px 18px',
                borderRadius: '999px',
                border: isSelected
                  ? '2px solid var(--accent)'
                  : '1px solid var(--border-strong)',
                background: isSelected ? 'var(--accent)' : 'var(--card-bg)',
                color: isSelected ? 'white' : 'var(--primary)',
                fontSize: '0.95rem',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              {category.name}
            </button>
          );
        })}
      </div>

      {/* Continue button */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
        <button
          onClick={handleContinue}
          disabled={saving}
          style={{
            padding: '12px 32px',
            borderRadius: '999px',
            border: 'none',
            background: 'var(--accent)',
            color: 'white',
            fontSize: '1rem',
            fontWeight: 500,
            cursor: saving ? 'default' : 'pointer',
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving
            ? 'Saving...'
            : selected.size > 0
              ? 'Continue'
              : 'Show me everything'}
        </button>
      </div>
    </div>
  );
}
